(() => {
  const roots = Array.from(document.querySelectorAll('[data-native-configurator]'));
  if (!roots.length) return;

  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
    style: 'currency', currency: window.Shark?.currency || 'EUR'
  }).format((Number(cents) || 0) / 100);
  const escapeHtml = value => String(value || '').replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[char]);
  const cleanHtml = html => {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    doc.querySelectorAll('script,style,iframe,object,embed').forEach(node => node.remove());
    return doc.body.innerHTML;
  };
  const productImage = product => {
    const featured = product?.featured_image;
    const first = product?.images?.[0];
    return (typeof featured === 'string' ? featured : featured?.src || featured?.url)
      || (typeof first === 'string' ? first : first?.src || first?.url) || '';
  };
  const productTags = product => Array.isArray(product?.tags)
    ? product.tags
    : String(product?.tags || '').split(',').map(tag => tag.trim()).filter(Boolean);
  const searchableProduct = product => `${product?.title || ''} ${productTags(product).join(' ')}`.toLowerCase();
  const loadCollection = async handle => {
    if (!handle) return [];
    const all = [];
    for (let page = 1; page <= 4; page += 1) {
      const response = await fetch(`/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error('Das Sortiment konnte gerade nicht geladen werden.');
      const products = (await response.json()).products || [];
      all.push(...products);
      if (products.length < 250) break;
    }
    return all;
  };

  const normalizeKey = value => String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const parseJson = (value, fallback) => {
    if (value && typeof value === 'object') return value;
    try { return JSON.parse(String(value || '')); }
    catch (error) { return fallback; }
  };
  const catalogProduct = item => {
    const flavors = parseJson(item?.flavor_profile_json, {});
    const dietary = parseJson(item?.dietary_tags_json, []);
    const flavorTags = Array.isArray(flavors)
      ? flavors
      : Object.entries(flavors || {}).filter(([, enabled]) => Boolean(enabled)).map(([tag]) => tag);
    const tags = [...new Set([...(Array.isArray(dietary) ? dietary : []), ...flavorTags])].filter(Boolean);
    const available = Number(item?.active) === 1 && Number(item?.available_quantity || 0) > 0;
    return {
      id: item.sku || item.title,
      title: item.title,
      featured_image: item.image_url || '',
      images: item.image_url ? [item.image_url] : [],
      tags,
      body_html: '',
      variants: [{ id: item.sku || item.title, sku: item.sku || '', available }],
      _erp: item
    };
  };
  const mergeCatalogProduct = (item, shopifyProduct) => {
    const product = catalogProduct(item);
    if (!shopifyProduct) return product;
    product.tags = [...new Set([...product.tags, ...productTags(shopifyProduct)])];
    product.body_html = shopifyProduct.body_html || '';
    if (!product.featured_image) {
      product.featured_image = productImage(shopifyProduct);
      product.images = product.featured_image ? [product.featured_image] : [];
    }
    return product;
  };

  roots.forEach(root => {
    const q = selector => root.querySelector(selector);
    const qa = selector => Array.from(root.querySelectorAll(selector));
    const mode = root.dataset.mode || 'candy';
    const steps = mode === 'muesli'
      ? ['packaging', 'size', 'ingredients', 'toppings', 'review']
      : ['packaging', 'size', 'ingredients', 'review'];
    const state = {
      step: 'packaging', packaging: '', base: null, ingredients: new Map(), toppings: new Map(),
      products: [], toppingProducts: [], catalog: null, catalogPromise: null, catalogError: '',
      advisorTerms: [], upsellsLoaded: false, lastAdvisorQuery: '', flowRevision: 0
    };
    const affinityKey = 'snacksharkMixAffinity';
    const readAffinity = () => {
      try { return JSON.parse(sessionStorage.getItem(affinityKey) || '{}'); }
      catch (error) { return {}; }
    };
    const recordAffinity = (query, productId) => {
      if (!query || !productId) return;
      const affinity = readAffinity();
      const key = query.toLowerCase().trim();
      affinity[key] = affinity[key] || {};
      affinity[key][String(productId)] = Number(affinity[key][String(productId)] || 0) + 1;
      try { sessionStorage.setItem(affinityKey, JSON.stringify(affinity)); } catch (error) {}
    };
    const status = q('[data-native-status]');
    const nextButtons = qa('[data-native-next], [data-native-next-top]');
    const back = q('[data-native-back]');
    const footerLabel = q('[data-footer-label]');
    const footerTotal = q('[data-footer-total]');

    const announce = message => {
      if (!status) return;
      status.textContent = '';
      requestAnimationFrame(() => { status.textContent = message; });
    };
    const loadCatalog = async () => {
      if (state.catalog) return state.catalog;
      if (state.catalogPromise) return state.catalogPromise;
      const catalogUrl = root.dataset.catalogUrl;
      if (!catalogUrl) throw new Error('Die Live-Verbindung zum Mix-Sortiment fehlt.');
      state.catalogPromise = fetch(catalogUrl, {
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }).then(async response => {
        if (!response.ok) throw new Error(`Das Live-Sortiment antwortet nicht (${response.status}).`);
        const payload = await response.json();
        if (!payload?.groups?.packaging || !payload?.groups?.candy || !payload?.groups?.cereal) {
          throw new Error('Das Live-Sortiment ist unvollständig.');
        }
        state.catalog = payload;
        state.catalogError = '';
        return payload;
      }).catch(error => {
        state.catalogError = error.message || 'Das Live-Sortiment konnte nicht geladen werden.';
        throw error;
      }).finally(() => { state.catalogPromise = null; });
      return state.catalogPromise;
    };

    const applyCatalogToBaseProducts = catalog => {
      const profile = catalog?.profiles?.[mode === 'muesli' ? 'cereal' : 'candy'] || {};
      const packaging = (catalog?.groups?.packaging || []).filter(item => {
        const itemMode = String(item.mix_type || '').toLowerCase();
        return !itemMode || itemMode === (mode === 'muesli' ? 'cereal' : 'candy');
      });
      const bySku = new Map(packaging.filter(item => item.sku).map(item => [String(item.sku).trim().toLowerCase(), item]));
      const byTitle = new Map(packaging.map(item => [normalizeKey(item.title), item]));
      qa('[data-base-product]').forEach(card => {
        const item = bySku.get(String(card.dataset.sku || '').trim().toLowerCase()) || byTitle.get(normalizeKey(card.dataset.title));
        if (!item) return;
        const pickCount = Number(item.max_units || profile.max_ingredients || card.dataset.pickCount || 0);
        const toppingCount = mode === 'muesli'
          ? Number(item.max_toppings ?? profile.max_toppings ?? card.dataset.toppingCount ?? 0)
          : 0;
        const available = Number(item.active) === 1 && Number(item.available_quantity || 0) > 0;
        card.dataset.pickCount = String(pickCount);
        card.dataset.toppingCount = String(toppingCount);
        card.dataset.erpSku = item.sku || '';
        card.dataset.erpAvailable = String(Number(item.available_quantity || 0));
        card.classList.toggle('is-soldout', !available);
        card.setAttribute('aria-disabled', available ? 'false' : 'true');
        const availability = card.querySelector('[data-base-availability]');
        const rule = card.querySelector('[data-base-rule]');
        if (availability) availability.textContent = available ? 'Live verfügbar' : 'Aktuell ausverkauft';
        if (rule) rule.textContent = `${pickCount} Sorten auswählbar${toppingCount > 0 ? ` + ${toppingCount} Toppings` : ''}`;
      });
    };
    const setDrawer = (drawer, open) => {
      if (!drawer) return;
      drawer.hidden = !open;
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.classList.toggle('no-scroll', open);
      if (open) setTimeout(() => drawer.querySelector('aside')?.focus?.(), 30);
    };
    const selectedTotal = map => Array.from(map.values()).reduce((sum, entry) => sum + entry.qty, 0);
    const selectedList = map => Array.from(map.values()).filter(entry => entry.qty > 0);
    const selectionText = map => selectedList(map).map(entry => `${entry.qty}× ${entry.product.title}`).join(', ');
    const hasProgress = () => Boolean(state.packaging || state.base || selectedTotal(state.ingredients) || selectedTotal(state.toppings));
    const setNext = (label, disabled) => nextButtons.forEach(button => {
      button.textContent = label;
      button.disabled = disabled;
    });

    const updateProgress = () => {
      const activeIndex = steps.indexOf(state.step);
      qa('[data-progress-step]').forEach(button => {
        const index = steps.indexOf(button.dataset.progressStep);
        button.classList.toggle('is-active', index === activeIndex);
        button.classList.toggle('is-complete', index < activeIndex);
        button.disabled = index > activeIndex || (index > 0 && !state.base);
      });
    };

    const renderSelectionDock = () => {
      const dock = q('[data-selection-dock]');
      const items = q('[data-selection-dock-items]');
      const count = q('[data-selection-dock-count]');
      if (!dock || !items) return;
      const currentKind = state.step === 'toppings' ? 'toppings' : 'ingredients';
      const list = currentKind === 'toppings' ? selectedList(state.toppings) : selectedList(state.ingredients);
      dock.hidden = !['ingredients', 'toppings'].includes(state.step) || !list.length;
      if (count) count.textContent = String(list.reduce((sum, entry) => sum + entry.qty, 0));
      items.innerHTML = list.map(entry => {
        const image = productImage(entry.product);
        return `<article class="native-selection-chip">
          ${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : '<span class="native-selection-chip__fallback">🍬</span>'}
          <span><strong>${escapeHtml(entry.product.title)}</strong><span class="native-selection-chip__qty"><button type="button" data-selection-adjust data-delta="-1" data-kind="${currentKind}" data-product-id="${entry.product.id}" aria-label="Weniger ${escapeHtml(entry.product.title)}">−</button><b>${entry.qty}</b><button type="button" data-selection-adjust data-delta="1" data-kind="${currentKind}" data-product-id="${entry.product.id}" aria-label="Mehr ${escapeHtml(entry.product.title)}">+</button></span></span>
          <button type="button" data-selection-remove data-kind="${currentKind}" data-product-id="${entry.product.id}" aria-label="${escapeHtml(entry.product.title)} entfernen">×</button>
        </article>`;
      }).join('');
    };

    const updateMeter = (type = 'ingredients') => {
      const isTopping = type === 'toppings';
      const map = isTopping ? state.toppings : state.ingredients;
      const total = selectedTotal(map);
      const limit = Number(isTopping ? state.base?.toppingCount : state.base?.pickCount) || 0;
      const countEl = q(isTopping ? '[data-topping-count]' : '[data-selection-count]');
      const limitEl = q(isTopping ? '[data-topping-limit]' : '[data-selection-limit]');
      const progress = q(isTopping ? '[data-topping-progress]' : '[data-selection-progress]');
      if (countEl) countEl.textContent = total;
      if (limitEl) limitEl.textContent = limit;
      if (progress) progress.style.width = `${Math.min(100, limit ? (total / limit) * 100 : 0)}%`;
      qa(`[data-ingredient-card][data-kind="${isTopping ? 'toppings' : 'ingredients'}"]`).forEach(card => {
        const quantity = Number(map.get(card.dataset.productId)?.qty || 0);
        card.classList.toggle('is-selected', quantity > 0);
        const output = card.querySelector('[data-card-qty]');
        const plus = card.querySelector('[data-card-plus]');
        if (output) output.textContent = quantity;
        if (plus) plus.disabled = total >= limit;
      });
      renderSelectionDock();
      return { total, limit };
    };

    const updateFooter = () => {
      const index = steps.indexOf(state.step);
      back.disabled = index === 0;
      footerTotal.textContent = state.base ? money(state.base.price) : '';
      if (state.step === 'packaging') {
        footerLabel.textContent = state.packaging ? 'Verpackung gewählt' : 'Wähle zuerst eine Verpackung';
        setNext('Größe wählen →', !state.packaging);
      } else if (state.step === 'size') {
        footerLabel.textContent = state.base ? state.base.title : 'Wähle deine Größe';
        setNext(mode === 'muesli' ? 'Zutaten wählen →' : 'Candies wählen →', !state.base);
      } else if (state.step === 'ingredients') {
        const meter = updateMeter('ingredients');
        footerLabel.textContent = `${meter.total} von ${meter.limit} ausgewählt`;
        setNext(mode === 'muesli' ? 'Zu den Toppings →' : 'Mix prüfen →', meter.total !== meter.limit);
      } else if (state.step === 'toppings') {
        const meter = updateMeter('toppings');
        footerLabel.textContent = `${meter.total} von bis zu ${meter.limit} Toppings`;
        setNext('Müsli prüfen →', false);
      } else {
        footerLabel.textContent = state.base?.title || 'Dein Mix';
        setNext('Mix in den Warenkorb', !q('[data-native-terms]')?.checked);
      }
    };

    const setStep = step => {
      if (!steps.includes(step)) return;
      state.step = step;
      qa('[data-screen]').forEach(screen => {
        const active = screen.dataset.screen === step;
        screen.hidden = !active;
        screen.classList.toggle('is-active', active);
      });
      updateProgress();
      updateFooter();
      renderSelectionDock();
      root.querySelector('.native-config__workspace')?.scrollTo?.({ top: 0, behavior: 'instant' });
    };

    const filterBaseProducts = () => {
      let visible = 0;
      const cards = qa('[data-base-wrap]');
      cards.forEach(wrapper => {
        const card = wrapper.querySelector('[data-base-product]');
        const show = wrapper.dataset.packaging === state.packaging && !card?.classList.contains('is-soldout');
        wrapper.hidden = !show;
        if (show) visible += 1;
      });
      const empty = q('[data-base-empty]');
      if (empty) empty.hidden = cards.length === 0 || visible > 0;
    };

    const renderProducts = (products, grid, kind) => {
      if (!grid) return;
      if (!products.length) {
        grid.innerHTML = '<p class="native-config__empty">Hier sind gerade keine verfügbaren Produkte hinterlegt.</p>';
        return;
      }
      grid.innerHTML = products.map(product => {
        const variant = (product.variants || []).find(item => item.available) || product.variants?.[0];
        if (!variant) return '';
        const tags = productTags(product);
        const badges = tags.filter(tag => /vegan|halal|sauer|zuckerfrei|frucht|schoko|crunch/i.test(tag)).slice(0, 2).map(tag => tag.replace(/^ck:/i, ''));
        const image = productImage(product);
        return `<article class="native-ingredient" data-ingredient-card data-kind="${kind}" data-product-id="${product.id}" data-search="${escapeHtml(searchableProduct(product))}">
          <button class="native-ingredient__info" type="button" data-card-info aria-label="Informationen zu ${escapeHtml(product.title)}">i</button>
          <div class="native-ingredient__media">${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : '<span>🍬</span>'}</div>
          <div class="native-ingredient__copy"><div class="native-ingredient__badges">${badges.map(tag => `<span>${escapeHtml(tag)}</span>`).join('')}</div><h3>${escapeHtml(product.title)}</h3></div>
          <div class="native-ingredient__quantity"><button type="button" data-card-minus aria-label="Weniger ${escapeHtml(product.title)}">−</button><strong data-card-qty>0</strong><button type="button" data-card-plus aria-label="Mehr ${escapeHtml(product.title)}">+</button></div>
        </article>`;
      }).join('');
    };

    const ensureProducts = async kind => {
      const isTopping = kind === 'toppings';
      const productsKey = isTopping ? 'toppingProducts' : 'products';
      if (state[productsKey].length) return state[productsKey];
      try {
        const [catalog, shopifyProducts] = await Promise.all([
          loadCatalog(),
          loadCollection(isTopping ? root.dataset.toppingCollection : root.dataset.ingredientCollection).catch(() => [])
        ]);
        const group = isTopping ? 'topping' : (mode === 'muesli' ? 'cereal' : 'candy');
        const profile = catalog?.profiles?.[mode === 'muesli' ? 'cereal' : 'candy'] || {};
        const shopifyBySku = new Map();
        const shopifyByTitle = new Map();
        shopifyProducts.forEach(product => {
          (product.variants || []).forEach(variant => {
            if (variant.sku) shopifyBySku.set(String(variant.sku).trim().toLowerCase(), product);
          });
          shopifyByTitle.set(normalizeKey(product.title), product);
        });
        state[productsKey] = (catalog?.groups?.[group] || [])
          .filter(item => Number(item.active) === 1)
          .filter(item => Number(profile.hide_sold_out || 1) !== 1 || Number(item.available_quantity || 0) > 0)
          .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0) || String(a.title || '').localeCompare(String(b.title || ''), 'de'))
          .map(item => mergeCatalogProduct(
            item,
            shopifyBySku.get(String(item.sku || '').trim().toLowerCase()) || shopifyByTitle.get(normalizeKey(item.title))
          ));
        renderProducts(state[productsKey], q(isTopping ? '[data-topping-grid]' : '[data-ingredient-grid]'), kind);
        updateMeter(kind);
      } catch (error) {
        const grid = q(isTopping ? '[data-topping-grid]' : '[data-ingredient-grid]');
        if (grid) grid.innerHTML = `<p class="native-config__empty">${escapeHtml(error.message)}</p>`;
      }
      return state[productsKey];
    };

    const applySearch = (input, kind, terms = []) => {
      const value = String(input?.value || '').trim().toLowerCase();
      const groups = terms.length ? terms.map(term => term.toLowerCase().split(/\s+/).filter(Boolean)) : [];
      const words = value.split(/\s+/).filter(Boolean);
      let visible = 0;
      const cards = qa(`[data-ingredient-card][data-kind="${kind}"]`);
      cards.forEach(card => {
        const search = card.dataset.search || '';
        const show = groups.length
          ? groups.every(group => group.some(word => search.includes(word)))
          : words.every(word => search.includes(word));
        card.hidden = !show;
        if (show) visible += 1;
      });
      const empty = kind === 'ingredients' ? q('[data-filter-empty]') : null;
      if (empty) empty.hidden = cards.length === 0 || visible > 0;
    };

    const resetDiscoveryFilters = () => {
      state.advisorTerms = [];
      state.lastAdvisorQuery = '';
      qa('[data-advisor-term]').forEach(node => node.classList.remove('is-selected'));
      const ingredientSearch = q('[data-ingredient-search]');
      const toppingSearch = q('[data-topping-search]');
      const favoriteSearch = q('[data-advisor-favorite]');
      if (ingredientSearch) ingredientSearch.value = '';
      if (toppingSearch) toppingSearch.value = '';
      if (favoriteSearch) favoriteSearch.value = '';
      const advisorResults = q('[data-advisor-results]');
      if (advisorResults) advisorResults.innerHTML = '';
      applySearch(ingredientSearch, 'ingredients');
      applySearch(toppingSearch, 'toppings');
    };

    const tasteProfile = () => {
      const profiles = [
        { key: 'sauer', test: /sauer|sour|acid/i, search: 'sauer', heading: 'Noch mehr Sauer-Power für später?' },
        { key: 'cola', test: /cola|kola/i, search: 'Cola', heading: 'Noch mehr Cola-Vibes für später?' },
        { key: 'tropisch', test: /trop|ananas|pineapple|mango|passion/i, search: 'tropisch', heading: 'Noch mehr Tropen-Vibes für später?' },
        { key: 'erdbeer', test: /erdbeer|strawberry/i, search: 'Erdbeer', heading: 'Noch mehr Erdbeer-Liebe für später?' },
        { key: 'blau', test: /blau|blue|schlumpf/i, search: 'blau', heading: 'Noch mehr blaue Lieblingssnacks?' },
        { key: 'schoko', test: /schoko|choco|kakao/i, search: 'Schokolade', heading: 'Noch mehr Schoko-Glück für später?' },
        { key: 'fruchtig', test: /frucht|fruit|gummi|bär|bear/i, search: 'Fruchtgummi', heading: 'Noch mehr fruchtige Entdeckungen?' }
      ];
      const scores = profiles.map(profile => ({ ...profile, score: selectedList(state.ingredients).reduce((sum, entry) => sum + (profile.test.test(searchableProduct(entry.product)) ? entry.qty : 0), 0) }));
      return scores.sort((a, b) => b.score - a.score)[0] || null;
    };

    const fetchSuggestedProducts = async profile => {
      if (!profile?.score) return [];
      const queries = [profile.search, `${profile.search} Snack`, `${profile.search} Süßigkeit`];
      const suggestionPages = await Promise.all(queries.map(async query => {
        try {
          const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`, { headers: { Accept: 'application/json' } });
          return response.ok ? (await response.json())?.resources?.results?.products || [] : [];
        } catch (error) { return []; }
      }));
      const products = Array.from(new Map(suggestionPages.flat().map(product => [String(product.id || product.url), product])).values());
      const hydrated = await Promise.all(products.slice(0, 24).map(async product => {
        const handle = String(product.url || '').match(/\/products\/([^?]+)/)?.[1];
        if (!handle) return product;
        try {
          const detail = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
          return detail.ok ? await detail.json() : product;
        } catch (error) { return product; }
      }));
      const paid = hydrated.filter(product => {
        const variant = (product.variants || []).find(item => item.available) || product.variants?.[0];
        return String(product.id) !== String(state.base?.productId) && Number(variant?.price || product.price || 0) > 0;
      });
      if (paid.length >= 4) return paid;
      const collectionMatches = [];
      try {
        const collectionResponse = await fetch('/collections.json?limit=250', { headers: { Accept: 'application/json' } });
        const collections = collectionResponse.ok ? (await collectionResponse.json()).collections || [] : [];
        const relevant = collections.filter(collection => profile.test.test(`${collection.title || ''} ${collection.handle || ''}`)).slice(0, 2);
        for (const collection of relevant) collectionMatches.push(...await loadCollection(collection.handle));
      } catch (error) {}
      const catalog = [];
      for (let page = 1; page <= 2 && catalog.length < 4; page += 1) {
        try {
          const catalogResponse = await fetch(`/products.json?limit=250&page=${page}`, { headers: { Accept: 'application/json' } });
          if (!catalogResponse.ok) break;
          const pageProducts = (await catalogResponse.json()).products || [];
          catalog.push(...pageProducts.filter(product => searchableProduct(product).includes(profile.search.toLowerCase())));
          if (pageProducts.length < 250) break;
        } catch (error) { break; }
      }
      const unique = new Map([...paid, ...collectionMatches, ...catalog].map(product => [String(product.id), product]));
      return Array.from(unique.values()).filter(product => {
        const variant = (product.variants || []).find(item => item.available) || product.variants?.[0];
        return String(product.id) !== String(state.base?.productId) && Number(variant?.price || product.price || 0) > 0;
      });
    };

    const loadUpsells = async () => {
      const section = q('[data-native-upsells]');
      const grid = q('[data-upsell-grid]');
      if (!section || !grid || !state.base?.productId || state.upsellsLoaded) return;
      state.upsellsLoaded = true;
      const profile = tasteProfile();
      const heading = q('[data-upsell-heading]');
      if (heading) heading.textContent = profile?.score ? profile.heading : 'Noch etwas Neues aus aller Welt?';
      try {
        let products = await fetchSuggestedProducts(profile);
        if (!products.length && !profile?.score) {
          const response = await fetch(`/recommendations/products.json?product_id=${encodeURIComponent(state.base.productId)}&limit=4&intent=related`);
          if (response.ok) products = (await response.json()).products || [];
        }
        grid.innerHTML = products.slice(0, 4).map(product => {
          const variant = (product.variants || []).find(item => item.available) || product.variants?.[0];
          if (!variant) return '';
          const image = productImage(product);
          return `<article class="native-upsell-card">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : '<span>🍭</span>'}<div><strong>${escapeHtml(product.title)}</strong><small>${money(variant.price || product.price)}</small></div><button type="button" data-upsell-add data-variant-id="${variant.id}">+ Dazu</button></article>`;
        }).join('');
        section.hidden = !grid.innerHTML.trim();
      } catch (error) {
        section.hidden = true;
      }
    };

    const showReview = () => {
      const productBox = q('[data-review-product]');
      if (productBox && state.base) productBox.innerHTML = `<span class="native-review__icon">${state.base.packaging === 'flasche' ? '🫙' : state.base.packaging === 'buchstaben_zahlen' ? 'A♥7' : state.base.packaging === 'sonderform' ? '💖' : '🍬'}</span><div><small>${escapeHtml(state.base.packagingLabel || '')}</small><h3>${escapeHtml(state.base.title)}</h3><strong>${money(state.base.price)}</strong></div>`;
      const ingredientBox = q('[data-review-ingredients]');
      if (ingredientBox) ingredientBox.innerHTML = selectedList(state.ingredients).map(entry => `<span>${entry.qty}× ${escapeHtml(entry.product.title)}</span>`).join('') || '<span>Keine Auswahl</span>';
      const toppingBox = q('[data-review-toppings]');
      if (toppingBox) toppingBox.innerHTML = selectedList(state.toppings).map(entry => `<span>${entry.qty}× ${escapeHtml(entry.product.title)}</span>`).join('') || '<span>Keine Toppings</span>';
      const price = q('[data-review-price]');
      if (price) price.textContent = money(state.base?.price || 0);
      const terms = q('[data-native-terms]');
      if (terms) terms.checked = false;
      state.upsellsLoaded = false;
      loadUpsells();
    };

    const addToCart = async () => {
      if (!state.base || !q('[data-native-terms]')?.checked) return;
      setNext('Mix wird vorbereitet …', true);
      const properties = {
        Candies: selectionText(state.ingredients), Extras: selectionText(state.toppings),
        Verpackung: state.base.packagingLabel || state.packaging,
        '_Configurator Units': '1', '_Configurator Version': 'SnackShark Native v2',
        _Packauftrag: JSON.stringify({
          mode,
          packaging_sku: state.base.sku || '',
          ingredients: selectedList(state.ingredients).map(entry => ({ sku: entry.product._erp?.sku || '', title: entry.product.title, quantity: entry.qty })),
          extras: selectedList(state.toppings).map(entry => ({ sku: entry.product._erp?.sku || '', title: entry.product.title, quantity: entry.qty }))
        })
      };
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: Number(state.base.variantId), quantity: 1, properties }] })
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || 'Der Mix konnte nicht hinzugefügt werden.');
        }
        try {
          sessionStorage.setItem('snacksharkLastMix', JSON.stringify({
            mode, path: window.location.pathname, packaging: state.packaging, base: state.base,
            ingredients: selectedList(state.ingredients).map(entry => ({ id: String(entry.product.id), qty: entry.qty })),
            toppings: selectedList(state.toppings).map(entry => ({ id: String(entry.product.id), qty: entry.qty }))
          }));
        } catch (error) {}
        const params = new URLSearchParams({ mix_added: '1' });
        const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (preview) params.set('preview_theme_id', preview);
        window.location.assign(`/?${params.toString()}`);
      } catch (error) {
        announce(error.message);
        updateFooter();
      }
    };

    const advance = () => {
      if (nextButtons[0]?.disabled) return;
      if (state.step === 'packaging') { filterBaseProducts(); setStep('size'); }
      else if (state.step === 'size') { ensureProducts('ingredients'); setStep('ingredients'); }
      else if (state.step === 'ingredients') {
        if (mode === 'muesli') { ensureProducts('toppings'); setStep('toppings'); }
        else { showReview(); setStep('review'); }
      } else if (state.step === 'toppings') { showReview(); setStep('review'); }
      else addToCart();
    };

    const resetFlow = () => {
      state.flowRevision += 1;
      state.step = 'packaging'; state.packaging = ''; state.base = null;
      state.ingredients.clear(); state.toppings.clear(); state.upsellsLoaded = false;
      qa('.is-selected').forEach(node => node.classList.remove('is-selected'));
      resetDiscoveryFilters();
      const upsells = q('[data-native-upsells]');
      if (upsells) upsells.hidden = true;
      qa('[data-base-product]').forEach(card => { card.hidden = false; });
      updateMeter('ingredients'); updateMeter('toppings'); setStep('packaging');
    };

    const exitNow = () => {
      document.body.classList.remove('native-config-flow-active');
      root.classList.remove('is-running');
      document.querySelector('.configurator-intro')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const askToExit = () => {
      if (!hasProgress()) { exitNow(); return; }
      const dialog = q('[data-native-exit-confirm]');
      if (dialog) { dialog.hidden = false; dialog.setAttribute('aria-hidden', 'false'); }
    };

    nextButtons.forEach(button => button.addEventListener('click', event => { event.stopPropagation(); advance(); }));
    back?.addEventListener('click', event => {
      event.stopPropagation();
      const index = steps.indexOf(state.step);
      if (index > 0) setStep(steps[index - 1]);
    });

    root.addEventListener('click', async event => {
      const packagePreview = event.target.closest('[data-package-preview]');
      if (packagePreview) {
        const body = q('[data-native-info-body]');
        if (body) body.innerHTML = `<p class="eyebrow">Echte Verpackung</p><h2 id="NativeInfoTitle">${escapeHtml(packagePreview.dataset.previewTitle || 'So sieht deine Verpackung aus')}</h2><img class="native-info__image native-info__image--package" src="${escapeHtml(packagePreview.dataset.previewImage)}" alt="${escapeHtml(packagePreview.dataset.previewTitle || 'Echte Verpackung')}"><p>Das Foto zeigt die aktuell hinterlegte Ausführung. Wenn sich das Verpackungsdesign ändert, wird nur dieses Vorschaubild ausgetauscht.</p>`;
        setDrawer(q('[data-native-info]'), true);
        return;
      }

      const packaging = event.target.closest('button.native-pack-card[data-packaging]');
      if (packaging) {
        resetDiscoveryFilters();
        state.packaging = packaging.dataset.packaging;
        qa('button.native-pack-card[data-packaging]').forEach(card => card.classList.toggle('is-selected', card === packaging));
        state.base = null; state.ingredients.clear(); state.toppings.clear();
        filterBaseProducts(); setStep('size');
        announce('Verpackung gewählt. Jetzt fehlt nur noch die Größe.');
        return;
      }

      const baseCard = event.target.closest('[data-base-product]');
      if (baseCard && !baseCard.classList.contains('is-soldout')) {
        const labels = { tuete: mode === 'muesli' ? 'Müsli-Tüte' : 'Wunschtüte', flasche: 'Wunschflasche', sonderform: 'Bucket & Sonderform', buchstaben_zahlen: 'Buchstaben & Zahlen' };
        state.base = {
          productId: baseCard.dataset.productId, variantId: baseCard.dataset.variantId,
          title: baseCard.dataset.title, price: Number(baseCard.dataset.price),
          pickCount: Number(baseCard.dataset.pickCount), toppingCount: Number(baseCard.dataset.toppingCount),
          packaging: baseCard.dataset.packaging, packagingLabel: labels[baseCard.dataset.packaging] || baseCard.dataset.packaging,
          sku: baseCard.dataset.erpSku || baseCard.dataset.sku || ''
        };
        state.ingredients.clear(); state.toppings.clear(); state.upsellsLoaded = false;
        qa('[data-base-product]').forEach(card => card.classList.toggle('is-selected', card === baseCard));
        setNext(mode === 'muesli' ? 'Zutaten werden geladen …' : 'Candies werden geladen …', true);
        await ensureProducts('ingredients');
        setStep('ingredients');
        announce(`${state.base.title} gewählt. Jetzt stellst du deinen Mix zusammen.`);
        return;
      }

      const ingredientCard = event.target.closest('[data-ingredient-card]');
      if (ingredientCard && (event.target.closest('[data-card-plus]') || event.target.closest('[data-card-minus]'))) {
        const isTopping = ingredientCard.dataset.kind === 'toppings';
        const map = isTopping ? state.toppings : state.ingredients;
        const products = isTopping ? state.toppingProducts : state.products;
        const product = products.find(item => String(item.id) === ingredientCard.dataset.productId);
        if (!product) return;
        const limit = Number(isTopping ? state.base?.toppingCount : state.base?.pickCount) || 0;
        const total = selectedTotal(map);
        const current = map.get(ingredientCard.dataset.productId) || { product, qty: 0 };
        if (event.target.closest('[data-card-plus]') && total < limit) current.qty += 1;
        if (event.target.closest('[data-card-minus]')) current.qty = Math.max(0, current.qty - 1);
        if (current.qty) map.set(ingredientCard.dataset.productId, current); else map.delete(ingredientCard.dataset.productId);
        if (!isTopping && current.qty && state.lastAdvisorQuery) recordAffinity(state.lastAdvisorQuery, product.id);
        updateMeter(isTopping ? 'toppings' : 'ingredients'); updateFooter();
        return;
      }

      if (ingredientCard && event.target.closest('[data-card-info]')) {
        const products = ingredientCard.dataset.kind === 'toppings' ? state.toppingProducts : state.products;
        const product = products.find(item => String(item.id) === ingredientCard.dataset.productId);
        const body = q('[data-native-info-body]');
        if (body && product) body.innerHTML = `<p class="eyebrow">Produktinfo</p><h2 id="NativeInfoTitle">${escapeHtml(product.title)}</h2>${productImage(product) ? `<img class="native-info__image" src="${escapeHtml(productImage(product))}" alt="${escapeHtml(product.title)}">` : ''}<div class="native-info__tags">${productTags(product).map(tag => `<span>${escapeHtml(tag)}</span>`).join('')}</div><div class="native-info__content">${cleanHtml(product.body_html) || '<p>Für dieses Produkt sind noch keine ausführlichen Zutaten- und Nährwertangaben hinterlegt. Es wird deshalb nicht als vollständig deklarierter Live-Artikel freigegeben.</p>'}</div>`;
        setDrawer(q('[data-native-info]'), true);
        return;
      }

      if (event.target.closest('[data-native-info-close]')) setDrawer(q('[data-native-info]'), false);
      if (event.target.closest('[data-native-advisor-open]')) setDrawer(q('[data-native-advisor]'), true);
      if (event.target.closest('[data-native-advisor-close]')) setDrawer(q('[data-native-advisor]'), false);
      const advisorChoice = event.target.closest('[data-advisor-term]');
      if (advisorChoice) advisorChoice.classList.toggle('is-selected');

      if (event.target.closest('[data-native-advisor-apply]')) {
        state.advisorTerms = qa('[data-advisor-term].is-selected').map(button => button.dataset.advisorTerm);
        const search = q('[data-ingredient-search]');
        if (search) search.value = state.advisorTerms.join(' ');
        setDrawer(q('[data-native-advisor]'), false);
        if (state.step !== 'ingredients') { await ensureProducts('ingredients'); setStep('ingredients'); }
        applySearch(search, 'ingredients', state.advisorTerms);
        announce(state.advisorTerms.length ? 'Filter aktiv – das Regal zeigt passende Sorten.' : 'Alle Filter wurden entfernt.');
      }

      if (event.target.closest('[data-advisor-recommend]')) {
        await ensureProducts('ingredients');
        const input = q('[data-advisor-favorite]');
        const result = q('[data-advisor-results]');
        const favorite = String(input?.value || '').trim().toLowerCase();
        if (!result) return;
        if (favorite.length < 2) { result.innerHTML = `<p>${mode === 'muesli' ? 'Nenne kurz dein Lieblings-Müsli, eine Zutat oder eine Geschmacksrichtung – dann legen wir los.' : 'Schreib kurz deine Lieblingssorte hinein – dann legen wir los.'}</p>`; return; }
        state.lastAdvisorQuery = favorite;
        const synonymGroups = mode === 'muesli' ? [
          [/schoko|choco|kakao/, ['schoko', 'choco', 'kakao', 'cookie']],
          [/erdbeer|strawberry|beere|berry/, ['erdbeer', 'strawberry', 'beere', 'berry', 'frucht']],
          [/nuss|nut|mandel|haselnuss/, ['nuss', 'nut', 'mandel', 'haselnuss']],
          [/crunch|knusper|knusprig/, ['crunch', 'crunchy', 'knusper', 'knusprig']],
          [/banane|banana/, ['banane', 'banana']], [/protein|fitness/, ['protein', 'fitness']]
        ] : [
          [/goldbär|goldbaer|gummibär|haribo/, ['bär', 'bear', 'frucht', 'gummi', 'monkey', 'affen', 'alpaka', 'schlangen', 'herzen', 'ringe']],
          [/pico|balla|gefüllt|gefuellt/, ['pico', 'balla', 'gefüllt', 'filled']],
          [/cola/, ['cola', 'kola']], [/schlumpf|schlümpf|blau|blue/, ['schlumpf', 'blau', 'blue', 'pixel', 'stern']],
          [/sauer|sour/, ['sauer', 'sour']], [/lakritz|salzig/, ['lakritz', 'salz']],
          [/erdbeer|strawberry/, ['erdbeer', 'strawberry']], [/tropisch|ananas|pineapple/, ['tropical', 'ananas', 'pineapple']]
        ];
        const terms = [...favorite.split(/\s+/), ...synonymGroups.filter(([test]) => test.test(favorite)).flatMap(([, words]) => words)];
        const affinity = readAffinity()[favorite] || {};
        const matches = state.products.map(product => ({ product, score: terms.reduce((score, term) => score + (searchableProduct(product).includes(term) ? 1 : 0), 0) + Number(affinity[String(product.id)] || 0) * 2 }))
          .filter(item => item.score > 0).sort((a, b) => b.score - a.score).slice(0, 6);
        result.innerHTML = matches.length ? `<p><strong>Das könnte deine nächste Lieblingssorte werden:</strong></p><div class="native-advisor__result-grid">${matches.map(({ product }) => {
          const image = productImage(product);
          return `<article>${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : `<span>${mode === 'muesli' ? '🥣' : '🍬'}</span>`}<strong>${escapeHtml(product.title)}</strong><button type="button" data-advisor-result-add data-product-id="${product.id}">Zum Mix +</button></article>`;
        }).join('')}</div>` : `<p>Direkt dafür haben wir noch keinen sauberen Treffer. Versuch ${mode === 'muesli' ? 'eine Zutat oder Richtung wie „Schoko“, „Erdbeere“ oder „crunchy“' : 'Geschmack oder Form, zum Beispiel „sauer“, „Cola“ oder „gefüllt“'}.</p>`;
      }

      const advisorAdd = event.target.closest('[data-advisor-result-add]');
      if (advisorAdd) {
        const product = state.products.find(item => String(item.id) === advisorAdd.dataset.productId);
        const limit = Number(state.base?.pickCount || 0);
        if (product && selectedTotal(state.ingredients) < limit) {
          const key = String(product.id);
          const current = state.ingredients.get(key) || { product, qty: 0 };
          current.qty += 1; state.ingredients.set(key, current);
          recordAffinity(state.lastAdvisorQuery, product.id);
          updateMeter('ingredients'); updateFooter();
          advisorAdd.textContent = '✓ Im Mix'; advisorAdd.disabled = true;
        }
      }

      if (event.target.closest('[data-native-surprise]')) {
        const limit = Number(state.base?.pickCount || 0);
        const available = state.products.slice().sort(() => Math.random() - .5).slice(0, limit);
        state.ingredients.clear();
        available.forEach(product => state.ingredients.set(String(product.id), { product, qty: 1 }));
        updateMeter('ingredients'); updateFooter();
        announce('Überraschungsmix ausgewählt – unten siehst du jede Sorte und kannst sie direkt entfernen.');
      }

      const remove = event.target.closest('[data-selection-remove]');
      if (remove) {
        const map = remove.dataset.kind === 'toppings' ? state.toppings : state.ingredients;
        map.delete(remove.dataset.productId);
        updateMeter(remove.dataset.kind); updateFooter();
      }
      const adjust = event.target.closest('[data-selection-adjust]');
      if (adjust) {
        const map = adjust.dataset.kind === 'toppings' ? state.toppings : state.ingredients;
        const entry = map.get(adjust.dataset.productId);
        const limit = Number(adjust.dataset.kind === 'toppings' ? state.base?.toppingCount : state.base?.pickCount) || 0;
        const delta = Number(adjust.dataset.delta || 0);
        if (entry && (delta < 0 || selectedTotal(map) < limit)) {
          entry.qty = Math.max(0, entry.qty + delta);
          if (entry.qty) map.set(adjust.dataset.productId, entry); else map.delete(adjust.dataset.productId);
          updateMeter(adjust.dataset.kind); updateFooter();
        }
      }
      if (event.target.closest('[data-selection-clear]')) {
        const kind = state.step === 'toppings' ? 'toppings' : 'ingredients';
        const map = kind === 'toppings' ? state.toppings : state.ingredients;
        map.clear(); updateMeter(kind); updateFooter(); announce('Auswahl geleert.');
      }
      const dockToggle = event.target.closest('[data-selection-dock-toggle]');
      if (dockToggle) {
        const dock = q('[data-selection-dock]');
        const expanded = dock?.classList.toggle('is-expanded');
        dockToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }

      const upsell = event.target.closest('[data-upsell-add]');
      if (upsell) {
        upsell.disabled = true; upsell.textContent = 'Wird hinzugefügt …';
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(upsell.dataset.variantId), quantity: 1 }] })
          });
          if (!response.ok) throw new Error('Nicht verfügbar');
          upsell.textContent = '✓ Im Warenkorb';
        } catch (error) { upsell.disabled = false; upsell.textContent = '+ Dazu'; announce('Das Zusatzprodukt konnte gerade nicht hinzugefügt werden.'); }
      }

      const progressButton = event.target.closest('[data-progress-step]');
      if (progressButton && !progressButton.disabled && steps.indexOf(progressButton.dataset.progressStep) < steps.indexOf(state.step)) setStep(progressButton.dataset.progressStep);
      if (event.target.closest('[data-native-exit]')) askToExit();
      if (event.target.closest('[data-native-exit-cancel]')) {
        const dialog = q('[data-native-exit-confirm]');
        if (dialog) { dialog.hidden = true; dialog.setAttribute('aria-hidden', 'true'); }
      }
      if (event.target.closest('[data-native-exit-discard]')) {
        const dialog = q('[data-native-exit-confirm]');
        if (dialog) { dialog.hidden = true; dialog.setAttribute('aria-hidden', 'true'); }
        resetFlow(); exitNow();
      }
    });

    q('[data-ingredient-search]')?.addEventListener('input', event => applySearch(event.target, 'ingredients'));
    q('[data-topping-search]')?.addEventListener('input', event => applySearch(event.target, 'toppings'));
    q('[data-native-terms]')?.addEventListener('change', updateFooter);

    const enterFlow = () => {
      document.body.classList.add('native-config-flow-active');
      root.classList.add('is-running');
      setStep(state.step);
    };
    const restoreLastMix = async () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('resume_mix') !== '1') return;
      params.delete('resume_mix');
      history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`);
      let saved;
      try { saved = JSON.parse(sessionStorage.getItem('snacksharkLastMix') || 'null'); } catch (error) { saved = null; }
      if (!saved || saved.mode !== mode || !saved.base) { announce('Der letzte Mix ist nicht mehr verfügbar. Starte bitte neu.'); return; }
      const restoreRevision = ++state.flowRevision;
      state.packaging = saved.packaging || saved.base.packaging;
      state.base = saved.base;
      filterBaseProducts();
      qa('button.native-pack-card[data-packaging]').forEach(card => card.classList.toggle('is-selected', card.dataset.packaging === state.packaging));
      qa('[data-base-product]').forEach(card => card.classList.toggle('is-selected', String(card.dataset.productId) === String(state.base.productId)));
      enterFlow();
      await ensureProducts('ingredients');
      if (restoreRevision !== state.flowRevision) return;
      state.ingredients.clear();
      (saved.ingredients || []).forEach(savedEntry => {
        const product = state.products.find(item => String(item.id) === String(savedEntry.id));
        if (product) state.ingredients.set(String(product.id), { product, qty: Number(savedEntry.qty) || 1 });
      });
      if (mode === 'muesli') {
        await ensureProducts('toppings');
        if (restoreRevision !== state.flowRevision) return;
        state.toppings.clear();
        (saved.toppings || []).forEach(savedEntry => {
          const product = state.toppingProducts.find(item => String(item.id) === String(savedEntry.id));
          if (product) state.toppings.set(String(product.id), { product, qty: Number(savedEntry.qty) || 1 });
        });
      }
      setStep('ingredients');
      updateMeter('ingredients');
      announce('Dein letzter Mix ist wieder da. Du kannst jede Sorte direkt ändern.');
    };
    document.querySelectorAll('a[href="#SnackSharkConfigurator"]').forEach(link => link.addEventListener('click', event => {
      event.preventDefault(); enterFlow();
    }));
    updateProgress(); updateFooter(); renderSelectionDock();
    loadCatalog().then(catalog => {
      applyCatalogToBaseProducts(catalog);
      filterBaseProducts();
      announce('Live-Sortiment verbunden. Bestände und Mix-Regeln sind aktuell.');
    }).catch(error => {
      announce(`${error.message} Bitte lade die Seite neu, bevor du bestellst.`);
      qa('[data-base-product]').forEach(card => {
        card.classList.add('is-soldout');
        card.setAttribute('aria-disabled', 'true');
      });
    });
    restoreLastMix();
  });
})();
